import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { Box, CssBaseline, useMediaQuery, useTheme as useMuiTheme } from '@mui/material'
import Login from './pages/Login'
import CleverCallback from './pages/CleverCallback'
import Sidebar from './components/Sidebar'
import TopHeader from './components/TopHeader'
import MirroringBanner from './components/MirroringBanner'
import LanguageSync from './components/LanguageSync'
import { ProtectedRoute } from './components/ProtectedRoute'
import { OrganizationThemedRoute } from './components/OrganizationThemedRoute'
import './App.css'

// Lazy load pages for better performance
import { lazy, Suspense, useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { debugPerformance } from './utils/debugPerformance'
import { useUser } from './contexts/UserContextNew'
import { auth } from './firebase'
import { signOut } from 'firebase/auth'
import { getEffectiveLinkageState } from './utils/accessLinkage'
import { ORG_DETAIL_SECTIONS } from './utils/orgConsoleNav'

const Landing = lazy(() => import('./pages/Landing'))
const Analysis = lazy(() => import('./pages/AnalysisNew'))
const Challenges = lazy(() => import('./pages/Challenges'))
const ChallengePage = lazy(() => import('./pages/ChallengePage'))
const Profile = lazy(() => import('./pages/Profile'))
const Account = lazy(() => import('./pages/Account'))
const Admin = lazy(() => import('./pages/Admin'))
const OrganizationManagement = lazy(() => import('./pages/OrganizationManagement'))
const OrganizationDetails = lazy(() => import('./pages/OrganizationDetails'))
const OrganizationAdmin = lazy(() => import('./pages/OrganizationAdmin'))
const ParticipationReport = lazy(() => import('./pages/ParticipationReport'))
const DepartmentBrief = lazy(() => import('./pages/DepartmentBrief'))
const OrgActivity = lazy(() => import('./pages/OrgActivity'))
const ClearanceDashboard = lazy(() => import('./pages/ClearanceDashboard'))
const JoinOrganization = lazy(() => import('./pages/JoinOrganization'))
const TeamManagement = lazy(() => import('./pages/TeamManagement'))
const FamilyHome = lazy(() => import('./pages/FamilyHome'))
const TeamProfile = lazy(() => import('./pages/TeamProfileFixed'))
const AccessDenied = lazy(() => import('./pages/AccessDenied'))
const TestPage = lazy(() => import('./pages/TestPage'))
const ThemeVariables = lazy(() => import('./pages/ThemeVariables'))
const EnterpriseInquiries = lazy(() => import('./pages/EnterpriseInquiries'))
const Messaging = lazy(() => import('./pages/Messaging'))
const Announcements = lazy(() => import('./pages/Announcements'))
const Notifications = lazy(() => import('./pages/Notifications'))
const Developer = lazy(() => import('./pages/Developer'))
// V1 Privacy: Groups removed - cross-org features disabled
// const Groups = lazy(() => import('./pages/Groups'))
// const GroupProfile = lazy(() => import('./pages/GroupProfile'))
const AuthAction = lazy(() => import('./pages/AuthAction'))
// V1 Privacy: Connections removed - cross-org features disabled
// const Connections = lazy(() => import('./pages/Connections'))
// Legal / Trust Center pages
const TrustCenter = lazy(() => import('./pages/TrustCenter'))
const FerpaCompliance = lazy(() => import('./pages/FerpaCompliance'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const SecurityPractices = lazy(() => import('./pages/SecurityPractices'))
const DataRetention = lazy(() => import('./pages/DataRetention'))
const AcceptableUse = lazy(() => import('./pages/AcceptableUse'))
// Marketing pages
const Features = lazy(() => import('./pages/Features'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Enterprise = lazy(() => import('./pages/Enterprise'))
const AccessSetup = lazy(() => import('./pages/AccessSetup'))
const DistrictAdmin = lazy(() => import('./pages/DistrictAdmin'))
const DistrictSync = lazy(() => import('./pages/DistrictSync'))
const DistrictHealth = lazy(() => import('./pages/DistrictHealth'))
const DistrictIntegration = lazy(() => import('./pages/DistrictIntegration'))
const DistrictSetup = lazy(() => import('./pages/DistrictSetup'))
const DistrictDirectory = lazy(() => import('./pages/DistrictDirectory'))
const DistrictTeams = lazy(() => import('./pages/DistrictTeams'))
const DistrictBrief = lazy(() => import('./pages/DistrictBrief'))
const DistrictAnnouncements = lazy(() => import('./pages/DistrictAnnouncements'))
const DistrictAcademic = lazy(() => import('./pages/DistrictAcademic'))
// COMMENTED OUT FOR FIRST RELEASE - Recruiting feature
// const RecruiterDashboard = lazy(() => import('./pages/RecruiterDashboard'))
// const AthleteDiscovery = lazy(() => import('./pages/AthleteDiscovery'))
// const TeamScoutingList = lazy(() => import('./pages/TeamScoutingList'))
// const TeamScoutingView = lazy(() => import('./pages/TeamScoutingView'))

const LoadingScreen = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="100vh"
  >
    <CircularProgress />
  </Box>
)

// Component to show Landing page for unauthenticated users and redirect to Teams for authenticated users
const LandingOrTeams = () => {
  const { user, loading, effectiveUserProfile } = useUser()

  if (loading) {
    return <LoadingScreen />
  }

  if (!user) return <Landing />;

  const linkage = getEffectiveLinkageState(effectiveUserProfile);
  const isSuperAdmin = effectiveUserProfile?.role === 'super_admin';

  // Linked users go to teams; unlinked users go to setup flow
  if (!isSuperAdmin && !linkage.hasAnyLinkedAccess) {
    return <Navigate to="/access/setup" replace />;
  }

  // A parent's day starts with their children, not with a team list
  if (effectiveUserProfile?.role === 'parent') {
    return <Navigate to="/home" replace />;
  }

  return <Navigate to="/teams" replace />;
}

/** `/home`: the parent home for parents; everyone else keeps landing on their teams. */
const HomeRoute = () => {
  const { loading, effectiveUserProfile } = useUser()
  if (loading) return <LoadingScreen />
  if (effectiveUserProfile?.role === 'parent') return <FamilyHome />
  return <Navigate to="/teams" replace />
}

function App() {
  const { user, loading: userLoading } = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const muiTheme = useMuiTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)

  useEffect(() => {
    // Enable performance debugging in development
    if (process.env.NODE_ENV === 'development') {
      debugPerformance();
    }
  }, []);

  useEffect(() => {
    // Auto-collapse sidebar on mobile
    if (isMobile) {
      setSidebarOpen(false)
    } else {
      setSidebarOpen(true)
    }
  }, [isMobile]);

  const handleLogout = async () => {
    try {
      await signOut(auth)
      navigate('/login')
    } catch (error) {    }
  }

  const handleSidebarToggle = () => {
    if (isMobile) {
      setMobileDrawerOpen(!mobileDrawerOpen)
    } else {
      setSidebarOpen(!sidebarOpen)
    }
  }

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false)
  }

  // Hide sidebar on landing page and login page
  const showSidebar = user && location.pathname !== '/login' && !(location.pathname === '/' && !user)

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      {/* Applies profile.preferred_language to the chrome (TRANSLATION_SPEC §13). */}
      <LanguageSync />

        {/* Sidebar */}
        {showSidebar && (
          <Sidebar
            open={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            onLogout={handleLogout}
            isMobile={isMobile}
            mobileOpen={mobileDrawerOpen}
            onMobileClose={handleMobileDrawerClose}
          />
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <MirroringBanner />
          {showSidebar && isMobile && (
            <TopHeader onMenuClick={handleSidebarToggle} isMobile />
          )}

          <Box sx={{ flexGrow: 1 }}>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                <Route path="/" element={<LandingOrTeams />} />
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <HomeRoute />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route
                  path="/access/setup"
                  element={
                    <ProtectedRoute>
                      <AccessSetup />
                    </ProtectedRoute>
                  }
                />
                <Route path="/auth/action" element={<AuthAction />} />
                <Route path="/auth/clever" element={<CleverCallback />} />
                <Route path="/join" element={<JoinOrganization />} />
                <Route path="/join/:inviteCode" element={<JoinOrganization />} />
                <Route path="/403" element={<AccessDenied />} />
                <Route path="/test" element={<TestPage />} />
                <Route path="/theme-variables" element={<ThemeVariables />} />

                {/* Legal / Trust Center Routes (Public) */}
                <Route path="/trust" element={<TrustCenter />} />
                <Route path="/ferpa" element={<FerpaCompliance />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/security" element={<SecurityPractices />} />
                <Route path="/retention" element={<DataRetention />} />
                <Route path="/acceptable-use" element={<AcceptableUse />} />

                {/* Marketing Pages (Public) */}
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/enterprise" element={<Enterprise />} />

                {/* Protected Routes */}
                <Route
                  path="/analysis"
                  element={
                    <ProtectedRoute requirePermission="can_access_analysis">
                      <Analysis />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/challenges"
                  element={
                    <ProtectedRoute requirePermission="can_access_challenges">
                      <OrganizationThemedRoute>
                        <Challenges />
                      </OrganizationThemedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/challenge/:challengeId"
                  element={
                    <ProtectedRoute requirePermission="can_access_challenges">
                      <OrganizationThemedRoute>
                        <ChallengePage />
                      </OrganizationThemedRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile/:userId?"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <Account />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute>
                      <Messaging />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/announcements"
                  element={
                    <ProtectedRoute>
                      <Announcements />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <Notifications />
                    </ProtectedRoute>
                  }
                />
                {/* COMMENTED OUT FOR FIRST RELEASE - Recruiting feature routes */}
                {/* <Route
                  path="/recruiter/dashboard"
                  element={
                    <ProtectedRoute>
                      <RecruiterDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recruiter/discover"
                  element={
                    <ProtectedRoute>
                      <AthleteDiscovery />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recruiter/following"
                  element={
                    <ProtectedRoute>
                      <AthleteDiscovery />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recruiter/teams"
                  element={
                    <ProtectedRoute>
                      <TeamScoutingList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recruiter/team/:teamId"
                  element={
                    <ProtectedRoute>
                      <TeamScoutingView />
                    </ProtectedRoute>
                  }
                /> */}
                <Route
                  path="/developer/*"
                  element={
                    <ProtectedRoute>
                      <Developer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute requirePermission="can_access_admin">
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin"
                  element={
                    <ProtectedRoute>
                      {/* Role check happens in-component (district_admin |
                          super_admin) — same pattern as parent-org-admin */}
                      <DistrictAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/sync"
                  element={
                    <ProtectedRoute>
                      <DistrictSync />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/sync/:runId"
                  element={
                    <ProtectedRoute>
                      <DistrictSync />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/health"
                  element={
                    <ProtectedRoute>
                      <DistrictHealth />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/integration"
                  element={
                    <ProtectedRoute>
                      <DistrictIntegration />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/integration/setup"
                  element={
                    <ProtectedRoute>
                      <DistrictSetup />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/directory"
                  element={
                    <ProtectedRoute>
                      <DistrictDirectory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/brief"
                  element={
                    <ProtectedRoute>
                      <DistrictBrief />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/teams"
                  element={
                    <ProtectedRoute>
                      <DistrictTeams />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/district-admin/announcements"
                  element={
                    <ProtectedRoute>
                      <DistrictAnnouncements />
                    </ProtectedRoute>
                  }
                />
                {/* Academic eligibility report (AUTOMATION_SPEC A11) */}
                <Route
                  path="/district-admin/academic"
                  element={
                    <ProtectedRoute>
                      <DistrictAcademic />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/enterprise-inquiries"
                  element={
                    <ProtectedRoute requirePermission="can_access_admin">
                      <EnterpriseInquiries />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/organizations/*"
                  element={
                    <ProtectedRoute requirePermission="can_manage_organizations">
                      <OrganizationManagement />
                    </ProtectedRoute>
                  }
                />
                {/* Organization console: one route per section, generated
                    from ORG_DETAIL_SECTIONS so adding a section is one entry
                    in utils/orgConsoleNav. The bare path redirects to the
                    first section in-component. Other `/organization/:id/*`
                    paths stay free for pages that own them. */}
                {['', ...ORG_DETAIL_SECTIONS.map((s) => `/${s.key}`)].map((suffix) => (
                  <Route
                    key={suffix || 'root'}
                    path={`/organization/:organizationId${suffix}`}
                    element={
                      <ProtectedRoute requirePermission="can_manage_organizations">
                        <OrganizationDetails />
                      </ProtectedRoute>
                    }
                  />
                ))}
                <Route
                  path="/org-admin/brief"
                  element={
                    <ProtectedRoute requirePermission="can_manage_organization_members">
                      <DepartmentBrief />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/org-admin/activity"
                  element={
                    <ProtectedRoute requirePermission="can_manage_organization_members">
                      <OrgActivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/org-admin/*"
                  element={
                    <ProtectedRoute requirePermission="can_manage_organization_members">
                      <OrganizationAdmin />
                    </ProtectedRoute>
                  }
                />
                {/* Org admins get the department; team staff arrive with
                    ?team= for their own team, so the route gate is team
                    management and the callable decides the rest. */}
                <Route
                  path="/participation-report"
                  element={
                    <ProtectedRoute requirePermission="can_access_team_management">
                      <ParticipationReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/clearance"
                  element={
                    <ProtectedRoute requirePermission="can_manage_organization_members">
                      <ClearanceDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams"
                  element={
                    <ProtectedRoute>
                      <TeamManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/team/:teamId"
                  element={
                    <ProtectedRoute>
                      <OrganizationThemedRoute>
                        <TeamProfile />
                      </OrganizationThemedRoute>
                    </ProtectedRoute>
                  }
                />
                {/* V1 Privacy: Groups and Connections removed - cross-org features disabled */}
                {/* <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
                <Route path="/group/:groupId" element={<ProtectedRoute><GroupProfile /></ProtectedRoute>} />
                <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} /> */}

                {/* Parent-org console routes removed — FROZEN per
                    HIERARCHY_SPEC §5. Districts are the umbrella now. */}
              </Routes>
            </Suspense>
          </Box>
        </Box>
      </Box>
  )
}

export default App
